import React, { useContext, useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import {
  FiBold,
  FiClock,
  FiImage,
  FiItalic,
  FiMessageCircle,
  FiPaperclip,
  FiSend,
  FiTrash2,
  FiUnderline,
  FiVideo,
  FiX,
} from "react-icons/fi";
import api from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import HistoricoContatoModal from "./HistoricoContatoModal";
import "./ContatoFlutuante.css";

const ASSUNTOS = [
  { value: "duvida", label: "D\u00favida" },
  { value: "erro", label: "Erro" },
  { value: "financeiro", label: "Financeiro" },
  { value: "melhoria", label: "Solicita\u00e7\u00e3o de Melhoria" },
];

const MAX_ANEXO_BYTES = 6 * 1024 * 1024;
const MAX_TOTAL_ANEXOS_BYTES = 8 * 1024 * 1024;

function formatarTamanho(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function arquivoParaBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ContatoFlutuante() {
  const { globalData } = useContext(GlobalContext);
  const { toast } = useFeedback();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState("duvida");
  const [titulo, setTitulo] = useState("");
  const [anexos, setAnexos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);

  const aplicarFormato = (comando) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(comando, false, null);
  };

  const inserirPreviewNoEditor = (file, previewUrl, id) => {
    if (!editorRef.current) return;

    const wrapper = document.createElement("div");
    wrapper.className = "contato-editor-anexo-preview";
    wrapper.contentEditable = "false";
    wrapper.dataset.anexoId = id;

    if (file.type.startsWith("image/")) {
      const img = document.createElement("img");
      img.src = previewUrl;
      img.alt = file.name;
      wrapper.appendChild(img);
    } else {
      const video = document.createElement("video");
      video.src = previewUrl;
      video.controls = true;
      wrapper.appendChild(video);
    }

    const legenda = document.createElement("span");
    legenda.textContent = `${file.name} (${formatarTamanho(file.size)})`;
    wrapper.appendChild(legenda);

    const linhaAntes = document.createElement("div");
    linhaAntes.innerHTML = "<br>";
    const linhaDepois = document.createElement("div");
    linhaDepois.innerHTML = "<br>";

    editorRef.current.appendChild(linhaAntes);
    editorRef.current.appendChild(wrapper);
    editorRef.current.appendChild(linhaDepois);
  };

  const adicionarArquivos = async (arquivos) => {
    const validos = arquivos.filter((file) => file.type.startsWith("image/") || file.type.startsWith("video/"));
    if (validos.length !== arquivos.length) {
      toast.warning("Somente imagens e v\u00eddeos podem ser anexados nesta mensagem.");
    }
    if (validos.length === 0) return;

    const totalAtual = anexos.reduce((soma, anexo) => soma + anexo.tamanho, 0);
    let totalNovo = totalAtual;
    const novos = [];

    for (const file of validos) {
      if (file.size > MAX_ANEXO_BYTES) {
        toast.warning(`O arquivo ${file.name} passa de 6 MB.`);
        continue;
      }
      totalNovo += file.size;
      if (totalNovo > MAX_TOTAL_ANEXOS_BYTES) {
        toast.warning("Os anexos somados devem ter no m\u00e1ximo 8 MB.");
        break;
      }

      const id = `${Date.now()}-${file.name}-${file.size}-${novos.length}`;
      const conteudoBase64 = await arquivoParaBase64(file);
      const previewUrl = URL.createObjectURL(file);
      novos.push({
        id,
        nome: file.name,
        tipo: file.type,
        tamanho: file.size,
        conteudo_base64: conteudoBase64,
        previewUrl,
      });
      inserirPreviewNoEditor(file, previewUrl, id);
    }

    if (novos.length > 0) {
      setAnexos((atuais) => [...atuais, ...novos]);
      toast.info("Arquivo anexado \u00e0 solicita\u00e7\u00e3o.");
    }
  };

  const handlePaste = async (event) => {
    const items = Array.from((event.clipboardData && event.clipboardData.items) || []);
    const arquivos = items
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (arquivos.length > 0) {
      event.preventDefault();
      await adicionarArquivos(arquivos);
      const texto = event.clipboardData && event.clipboardData.getData("text/plain");
      if (texto) document.execCommand("insertText", false, texto);
    }
  };

  const handleSelecionarArquivos = async (event) => {
    const arquivos = Array.from(event.target.files || []);
    if (arquivos.length > 0) await adicionarArquivos(arquivos);
    event.target.value = "";
  };

  const removerAnexo = (id) => {
    setAnexos((atuais) => {
      const removido = atuais.find((anexo) => anexo.id === id);
      if (removido && removido.previewUrl) URL.revokeObjectURL(removido.previewUrl);
      if (editorRef.current) {
        const preview = Array.from(editorRef.current.querySelectorAll("[data-anexo-id]"))
          .find((node) => node.dataset.anexoId === id);
        if (preview) preview.remove();
      }
      return atuais.filter((anexo) => anexo.id !== id);
    });
  };

  const limparFormulario = () => {
    setTipo("duvida");
    setTitulo("");
    anexos.forEach((anexo) => {
      if (anexo.previewUrl) URL.revokeObjectURL(anexo.previewUrl);
    });
    setAnexos([]);
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  const enviar = async (event) => {
    event.preventDefault();
    const mensagemHtml = (editorRef.current && editorRef.current.innerHTML) || "";
    const mensagemTexto = (editorRef.current && editorRef.current.innerText) || "";
    const idEntidade = (globalData && globalData.entidade_id) || localStorage.getItem("entidade_id");

    if (!idEntidade) {
      toast.warning("Selecione uma entidade antes de enviar a solicita\u00e7\u00e3o.");
      return;
    }
    if (!titulo.trim()) {
      toast.warning("Informe um t\u00edtulo para a solicita\u00e7\u00e3o.");
      return;
    }
    if (mensagemTexto.trim().length < 10 && anexos.length === 0) {
      toast.warning("Descreva a solicita\u00e7\u00e3o com um pouco mais de detalhe.");
      return;
    }

    setEnviando(true);
    try {
      await api.post(
        "/api/contato/solicitacao",
        {
          id_entidade: idEntidade,
          tipo,
          titulo: titulo.trim(),
          mensagem_html: mensagemHtml,
          mensagem_texto: mensagemTexto,
          anexos: anexos.map(({ nome, tipo: tipoAnexo, tamanho, conteudo_base64 }) => ({
            nome,
            tipo: tipoAnexo,
            tamanho,
            conteudo_base64,
          })),
        },
        { headers: { id_entidade: idEntidade } }
      );
      toast.success("Solicita\u00e7\u00e3o enviada para a Duxen.");
      limparFormulario();
      setAberto(false);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.message) || "N\u00e3o foi poss\u00edvel enviar a solicita\u00e7\u00e3o.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="contato-flutuante">
      {aberto && (
        <section className="contato-painel" aria-label="Contato com a Duxen">
          <div className="contato-painel-header">
            <div>
              <strong>Contato</strong>
              <span>{"D\u00favidas, erros e melhorias"}</span>
            </div>
            <button type="button" className="contato-fechar" onClick={() => setAberto(false)} aria-label="Fechar contato">
              <FiX />
            </button>
          </div>

          <Form onSubmit={enviar}>
            <Form.Group className="mb-2" controlId="contato-assunto">
              <Form.Label>Assunto</Form.Label>
              <Form.Select size="sm" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {ASSUNTOS.map((assunto) => (
                  <option key={assunto.value} value={assunto.value}>
                    {assunto.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-2" controlId="contato-titulo">
              <Form.Label>{"T\u00edtulo"}</Form.Label>
              <Form.Control
                size="sm"
                maxLength={140}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder={"Resumo da solicita\u00e7\u00e3o"}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="contato-mensagem">
              <Form.Label>Mensagem</Form.Label>
              <div className="contato-editor-toolbar" aria-label={"Formata\u00e7\u00e3o da mensagem"}>
                <button type="button" onClick={() => aplicarFormato("bold")} title="Negrito">
                  <FiBold />
                </button>
                <button type="button" onClick={() => aplicarFormato("italic")} title={"It\u00e1lico"}>
                  <FiItalic />
                </button>
                <button type="button" onClick={() => aplicarFormato("underline")} title="Sublinhado">
                  <FiUnderline />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (fileInputRef.current) fileInputRef.current.click();
                  }}
                  title={"Anexar imagem ou v\u00eddeo"}
                >
                  <FiPaperclip />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="contato-file-input"
                  onChange={handleSelecionarArquivos}
                />
              </div>
              <div
                ref={editorRef}
                className="contato-editor"
                contentEditable
                role="textbox"
                aria-multiline="true"
                onPaste={handlePaste}
                data-placeholder={"Descreva aqui o que aconteceu ou o que voc\u00ea precisa..."}
              />
            </Form.Group>

            {anexos.length > 0 && (
              <div className="contato-anexos">
                {anexos.map((anexo) => (
                  <div className="contato-anexo" key={anexo.id}>
                    <span className="contato-anexo-icone">
                      {anexo.tipo.startsWith("image/") ? <FiImage /> : <FiVideo />}
                    </span>
                    <span className="contato-anexo-nome">{anexo.nome}</span>
                    <span className="contato-anexo-tamanho">{formatarTamanho(anexo.tamanho)}</span>
                    <button type="button" onClick={() => removerAnexo(anexo.id)} title="Remover anexo">
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="contato-acoes">
              <Button
                type="button"
                variant="info"
                size="sm"
                className="contato-historico-btn"
                onClick={() => setShowHistorico(true)}
              >
                <FiClock /> Histórico
              </Button>
              <Button type="button" variant="outline-secondary" size="sm" onClick={limparFormulario} disabled={enviando}>
                Limpar
              </Button>
              <Button type="submit" size="sm" disabled={enviando}>
                {enviando ? <Spinner animation="border" size="sm" /> : <FiSend />}
                Enviar
              </Button>
            </div>
          </Form>
        </section>
      )}

      <button
        type="button"
        className="contato-botao"
        onClick={() => setAberto((atual) => !atual)}
        aria-label={aberto ? "Fechar contato" : "Abrir contato"}
        aria-expanded={aberto}
      >
        {aberto ? <FiX /> : <FiMessageCircle />}
      </button>

      <HistoricoContatoModal show={showHistorico} onHide={() => setShowHistorico(false)} />
    </div>
  );
}

export default ContatoFlutuante;
